import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PiCheckCircleLight,
  PiBookOpenTextLight,
  PiUserLight,
  PiBuildingsLight,
  PiGlobeLight,
  PiSpinnerGapLight,
  PiWarningCircleLight,
  PiShieldCheckLight,
} from 'react-icons/pi';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import apis from '../../utils/apis';
import { PlaybookListItem } from '../../types/playbook';
import Spinner from '../shared/TailwindComponents/Spinner';
import PersonCell from '../shared/access/PersonCell';
import { ownerOf } from '../shared/access/accessModel';

// ─── Risk weight color coding ────────────────────────────────────────────────

export const getRiskWeightColor = (weight: number | null | undefined): string => {
  if (weight == null) return 'bg-gray-100 dark:bg-gray-700 text-gray-500';
  if (weight >= 9) return 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-400';
  if (weight >= 6) return 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400';
  if (weight >= 3) return 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400';
  return 'bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-400';
};

export const getRiskWeightLabel = (weight: number | null | undefined): string => {
  if (weight == null) return '';
  if (weight >= 9) return 'Critical';
  if (weight >= 6) return 'High';
  if (weight >= 3) return 'Medium';
  return 'Low';
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface PlaybookPickerModalProps {
  selectedPlaybookUuid: string;
  onSelect: (playbook: PlaybookListItem | null) => void;
  onClose: () => void;
}

type FilterTab = 'all' | 'mine' | 'firm' | 'global';

// ─── Component ───────────────────────────────────────────────────────────────

const PlaybookPickerModal: React.FC<PlaybookPickerModalProps> = ({
  selectedPlaybookUuid,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [pendingUuid, setPendingUuid] = useState<string>(selectedPlaybookUuid);

  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetch playbooks
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await apis.get('/playbooks', {
          params: { page: 1, page_size: 100 },
        });
        if (response.status === 200) {
          setPlaybooks(response.data.items || []);
        }
      } catch {
        setLoadError(t('Failed to load playbooks. Please try again.'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Counts per filter tab
  const counts = useMemo(() => {
    return {
      all: playbooks.length,
      global: playbooks.filter((p) => p.is_global).length,
      firm: playbooks.filter((p) => !p.is_global).length,
      mine: playbooks.filter((p) => !p.is_global && p.has_access?.length > 0).length,
    };
  }, [playbooks]);

  // Apply filter + search
  const filtered = useMemo(() => {
    let list = playbooks;

    if (activeFilter === 'global') list = list.filter((p) => p.is_global);
    else if (activeFilter === 'firm') list = list.filter((p) => !p.is_global);
    else if (activeFilter === 'mine') list = list.filter((p) => !p.is_global && p.has_access?.length > 0);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.agreement_types?.some((a) => a.toLowerCase().includes(q)) ||
          p.jurisdictions?.some((j) => j.toLowerCase().includes(q))
      );
    }

    return list;
  }, [playbooks, activeFilter, search]);

  const handleCardClick = (uuid: string) => {
    setPendingUuid(uuid === pendingUuid ? '' : uuid);
  };

  const handleConfirm = () => {
    const selected = playbooks.find((p) => p.uuid === pendingUuid) || null;
    onSelect(selected);
    onClose();
  };

  const filterTabs: { key: FilterTab; label: string; icon: React.ReactNode; count: number }[] = [
    { key: 'all', label: t('All'), icon: <PiBookOpenTextLight className="w-4 h-4" />, count: counts.all },
    { key: 'mine', label: t('Shared With Me'), icon: <PiUserLight className="w-4 h-4" />, count: counts.mine },
    { key: 'firm', label: t('Firm'), icon: <PiBuildingsLight className="w-4 h-4" />, count: counts.firm },
    { key: 'global', label: t('PONS'), icon: <PiGlobeLight className="w-4 h-4" />, count: counts.global },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[300000000] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative z-10 w-[95vw] max-w-5xl h-[85vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary-50 dark:bg-primary-900/30">
                <PiShieldCheckLight className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('Select Playbook')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('Choose a playbook to run compliance checks against each document')}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={onClose}
            >
              {t('Close')}
            </Button>
          </div>

          {/* Body: sidebar + content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar — filters */}
            <div className="w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-col gap-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold mb-2 px-2">
                {t('Filter by')}
              </p>
              {filterTabs.map((tab) => (
                <button
                  type="button"
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full text-left ${
                    activeFilter === tab.key
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {tab.icon}
                  <span className="flex-1">{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeFilter === tab.key
                      ? 'bg-primary-100 dark:bg-primary-800/50 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Right content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search bar */}
              <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={t('Search playbooks...')}
                />
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {isLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Spinner size="glyph" className="!h-6 !w-6" />
                  </div>
                ) : loadError ? (
                  <div className="text-center py-16">
                    <PiWarningCircleLight className="w-12 h-12 text-danger-300 dark:text-danger-700 mx-auto mb-3" />
                    <p className="text-sm text-danger-500 dark:text-danger-400">{loadError}</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-16">
                    <PiBookOpenTextLight className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {search ? t('No playbooks match your search') : t('No playbooks available')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {filtered.map((pb) => {
                      const isSelected = pb.uuid === pendingUuid;
                      return (
                        <motion.div
                          key={pb.uuid}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => handleCardClick(pb.uuid)}
                          className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all hover:shadow-md ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10 shadow-sm ring-1 ring-primary-200 dark:ring-primary-800'
                              : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 bg-white dark:bg-gray-800/50'
                          }`}
                        >
                          {/* Selected checkmark */}
                          {isSelected && (
                            <div className="absolute top-3 right-3">
                              <PiCheckCircleLight className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                          )}

                          {/* Playbook header */}
                          <div className="flex items-start gap-3 mb-3">
                            <div className={`p-2 rounded-lg flex-shrink-0 ${isSelected ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-gray-100 dark:bg-gray-700'}`}>
                              <PiShieldCheckLight className={`w-4 h-4 ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate pr-6">
                                {pb.name}
                              </h3>
                              {pb.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {pb.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <Badge color={isSelected ? 'blue' : 'gray'} className="text-[10px]">
                                  {pb.clause_count} {t('clauses')}
                                </Badge>
                                {pb.is_global ? (
                                  <Badge color="purple" className="text-[10px]">
                                    {t('By PONS')}
                                  </Badge>
                                ) : (
                                  /*
                                    THE CREATOR AS A PERSON, not a text badge.

                                    This drew `owner.name` inside a Badge, so
                                    the picture on `PlaybookSharedUser.
                                    profile_picture` was never asked for — the
                                    same playbook showed a face in the
                                    templates list and a blue pill here.

                                    It also fell back to `has_access[0]` when
                                    no entry claimed admin, which credits a
                                    shared playbook to whoever the backend
                                    listed first. `ownerOf` returns undefined
                                    instead, and PersonCell then draws nothing
                                    at all rather than a guess.
                                  */
                                  (() => {
                                    const owner = ownerOf(pb.has_access);
                                    return owner ? (
                                      <PersonCell person={owner} size="xs" />
                                    ) : null;
                                  })()
                                )}
                                {pb.agreement_types?.slice(0, 2).map((type) => (
                                  <Badge key={type} color="gray" className="text-[10px] capitalize">
                                    {type.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Jurisdiction tags */}
                          {pb.jurisdictions && pb.jurisdictions.length > 0 && (
                            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                              {pb.jurisdictions.slice(0, 3).map((j) => (
                                <span
                                  key={j}
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium"
                                >
                                  {j}
                                </span>
                              ))}
                              {pb.jurisdictions.length > 3 && (
                                <span className="text-[10px] text-gray-400">
                                  +{pb.jurisdictions.length - 3} {t('more')}
                                </span>
                              )}
                            </div>
                          )}

                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {filtered.length} {t('playbooks available')}
              {pendingUuid && (
                <span className="ml-2 text-primary-600 dark:text-primary-400 font-medium">
                  — 1 {t('selected')}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {pendingUuid && (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={() => {
                    onSelect(null);
                    onClose();
                  }}
                >
                  {t('Clear Selection')}
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={handleConfirm}
                disabled={!pendingUuid}
              >
                {t('Confirm')}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PlaybookPickerModal;
